import { GPU } from './gpuData';

export interface PricePoint {
  date: string;
  price: number;
}

export interface GPUPriceHistory {
  gpuId: string;
  msrp: number;
  currentPrice: number;
  lowestPrice: number;
  highestPrice: number;
  priceHistory: PricePoint[];
}

// Generate mock price history data for the past 6 months
function generatePriceHistory(msrp: number, volatility: number = 0.15): PricePoint[] {
  const history: PricePoint[] = [];
  const today = new Date();
  let currentPrice = msrp * (1 + Math.random() * 0.3); // Start higher than MSRP
  
  for (let i = 180; i >= 0; i -= 7) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Simulate price fluctuation trending toward MSRP
    const trend = (msrp - currentPrice) * 0.02; // Slight trend toward MSRP
    const noise = (Math.random() - 0.5) * msrp * volatility;
    currentPrice = Math.max(msrp * 0.85, currentPrice + trend + noise);
    
    history.push({
      date: date.toISOString().split('T')[0],
      price: Math.round(currentPrice),
    });
  }
  
  return history;
}

// Generate price data for all GPUs
export const gpuPriceData: GPUPriceHistory[] = [
  { gpuId: 'rtx-5090', msrp: 1999, currentPrice: 2199, lowestPrice: 1999, highestPrice: 2499, priceHistory: generatePriceHistory(1999, 0.1) },
  { gpuId: 'rtx-4090', msrp: 1599, currentPrice: 1649, lowestPrice: 1499, highestPrice: 1899, priceHistory: generatePriceHistory(1599, 0.12) },
  { gpuId: 'rtx-5080', msrp: 999, currentPrice: 1099, lowestPrice: 999, highestPrice: 1249, priceHistory: generatePriceHistory(999, 0.1) },
  { gpuId: 'rtx-4080-super', msrp: 999, currentPrice: 979, lowestPrice: 899, highestPrice: 1099, priceHistory: generatePriceHistory(999, 0.12) },
  { gpuId: 'rtx-5070-ti', msrp: 749, currentPrice: 799, lowestPrice: 749, highestPrice: 899, priceHistory: generatePriceHistory(749, 0.1) },
  { gpuId: 'rtx-4080', msrp: 1199, currentPrice: 999, lowestPrice: 899, highestPrice: 1299, priceHistory: generatePriceHistory(1199, 0.15) },
  { gpuId: 'rtx-4070-ti-super', msrp: 799, currentPrice: 779, lowestPrice: 699, highestPrice: 899, priceHistory: generatePriceHistory(799, 0.12) },
  { gpuId: 'rtx-5070', msrp: 549, currentPrice: 599, lowestPrice: 549, highestPrice: 699, priceHistory: generatePriceHistory(549, 0.1) },
  { gpuId: 'rtx-4070-ti', msrp: 799, currentPrice: 699, lowestPrice: 649, highestPrice: 899, priceHistory: generatePriceHistory(799, 0.15) },
  { gpuId: 'rtx-4070-super', msrp: 599, currentPrice: 579, lowestPrice: 529, highestPrice: 699, priceHistory: generatePriceHistory(599, 0.12) },
  { gpuId: 'rtx-4070', msrp: 549, currentPrice: 519, lowestPrice: 479, highestPrice: 649, priceHistory: generatePriceHistory(549, 0.15) },
  { gpuId: 'rtx-4060-ti', msrp: 399, currentPrice: 379, lowestPrice: 349, highestPrice: 449, priceHistory: generatePriceHistory(399, 0.12) },
  { gpuId: 'rtx-4060', msrp: 299, currentPrice: 289, lowestPrice: 269, highestPrice: 349, priceHistory: generatePriceHistory(299, 0.1) },
  { gpuId: 'rx-7900-xtx', msrp: 999, currentPrice: 899, lowestPrice: 799, highestPrice: 1099, priceHistory: generatePriceHistory(999, 0.18) },
  { gpuId: 'rx-9070-xt', msrp: 649, currentPrice: 699, lowestPrice: 649, highestPrice: 799, priceHistory: generatePriceHistory(649, 0.1) },
  { gpuId: 'rx-7900-xt', msrp: 899, currentPrice: 749, lowestPrice: 699, highestPrice: 999, priceHistory: generatePriceHistory(899, 0.18) },
  { gpuId: 'rx-9070', msrp: 549, currentPrice: 579, lowestPrice: 549, highestPrice: 649, priceHistory: generatePriceHistory(549, 0.1) },
  { gpuId: 'rx-7900-gre', msrp: 549, currentPrice: 499, lowestPrice: 449, highestPrice: 599, priceHistory: generatePriceHistory(549, 0.15) },
  { gpuId: 'rx-7800-xt', msrp: 499, currentPrice: 459, lowestPrice: 429, highestPrice: 549, priceHistory: generatePriceHistory(499, 0.12) },
  { gpuId: 'rx-7700-xt', msrp: 449, currentPrice: 399, lowestPrice: 369, highestPrice: 499, priceHistory: generatePriceHistory(449, 0.15) },
  { gpuId: 'rx-7600-xt', msrp: 329, currentPrice: 309, lowestPrice: 289, highestPrice: 369, priceHistory: generatePriceHistory(329, 0.1) },
  { gpuId: 'rx-7600', msrp: 269, currentPrice: 249, lowestPrice: 229, highestPrice: 299, priceHistory: generatePriceHistory(269, 0.12) },
  { gpuId: 'arc-b580', msrp: 249, currentPrice: 259, lowestPrice: 249, highestPrice: 299, priceHistory: generatePriceHistory(249, 0.08) },
  { gpuId: 'arc-a770', msrp: 349, currentPrice: 299, lowestPrice: 269, highestPrice: 399, priceHistory: generatePriceHistory(349, 0.18) },
  { gpuId: 'arc-a750', msrp: 289, currentPrice: 249, lowestPrice: 219, highestPrice: 329, priceHistory: generatePriceHistory(289, 0.18) },
];

export function getPriceHistoryForGPU(gpuId: string): GPUPriceHistory | undefined {
  return gpuPriceData.find(p => p.gpuId === gpuId);
}

export function getPriceChange(priceHistory: GPUPriceHistory): { change: number; percentage: number } {
  const history = priceHistory.priceHistory;
  if (history.length < 2) return { change: 0, percentage: 0 };
  
  const oldPrice = history[0].price;
  const newPrice = history[history.length - 1].price;
  const change = newPrice - oldPrice;
  const percentage = (change / oldPrice) * 100;
  
  return { change, percentage };
}
